// Small presentational pieces shared across the analytics pages: sentiment
// dots, mode chips, the range preset picker and the input-mode segment toggle.

import type { AnalysisStatus, CallRecord, CallSource } from "../../services/analytics";
import { ENGINE_LABEL, ENGINE_ORDER, engineLabel } from "../voiceEngines/labels";
import {
  formatDateTime,
  formatDurationMs,
  formatSentiment,
  inputLabel,
  sentimentTone,
  type EngineFilter,
  type InputModeFilter,
  type RangePreset,
  type SourceFilter,
  RANGE_PRESETS,
} from "./format";

export function SentimentDot({ score }: { score: number | null | undefined }) {
  return <span className={`sentiment-dot ${sentimentTone(score)}`} title={formatSentiment(score)} />;
}

export function SentimentCell({ score }: { score: number | null | undefined }) {
  return (
    <span className="sentiment-cell">
      <SentimentDot score={score} />
      <span>{formatSentiment(score)}</span>
    </span>
  );
}

export function ModeChip({ call }: { call: CallRecord }) {
  const { tone, label, title } = inputLabel(call);
  return <span className={`mode-chip ${tone}`} title={title}>{label}</span>;
}

// Bench traffic is the exception, so app calls get no chip.
export function SourceChip({ source }: { source: CallSource }) {
  if (source !== "studio_bench") return null;
  return <span className="chip chip-bench" title="Studio voice-bench test call">bench</span>;
}

export function AnalysisBadge({ status }: { status: AnalysisStatus }) {
  if (status === "complete") return null;
  return <span className={`badge analysis-${status}`}>analysis {status}</span>;
}

export function RangePicker({
  value,
  onChange,
}: {
  value: RangePreset;
  onChange: (preset: RangePreset) => void;
}) {
  return (
    <div className="seg" role="group" aria-label="Date range">
      {RANGE_PRESETS.map((p) => (
        <button
          key={p.key}
          className={value === p.key ? "active" : ""}
          onClick={() => onChange(p.key)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// Filters on what the user did (guide_call_records.input_mode), not the
// pipeline. Anything but All leaves out rows with no input mode.
const INPUT_MODE_OPTIONS: { key: InputModeFilter; label: string }[] = [
  { key: "voice", label: "Voice" },
  { key: "text", label: "Typed" },
  { key: "mixed", label: "Mixed" },
  { key: "all", label: "All" },
];

export function InputModeToggle({
  value,
  onChange,
}: {
  value: InputModeFilter;
  onChange: (mode: InputModeFilter) => void;
}) {
  return (
    <div className="seg" role="group" aria-label="Input mode">
      {INPUT_MODE_OPTIONS.map((m) => (
        <button
          key={m.key}
          className={value === m.key ? "active" : ""}
          onClick={() => onChange(m.key)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

const SOURCE_OPTIONS: { key: SourceFilter; label: string }[] = [
  { key: "app", label: "App" },
  { key: "studio_bench", label: "Bench" },
  { key: "all", label: "All" },
];

export function TrafficToggle({
  value,
  onChange,
}: {
  value: SourceFilter;
  onChange: (source: SourceFilter) => void;
}) {
  return (
    <div className="seg" role="group" aria-label="Traffic source" title="Traffic source">
      {SOURCE_OPTIONS.map((s) => (
        <button
          key={s.key}
          className={value === s.key ? "active" : ""}
          onClick={() => onChange(s.key)}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

// Voice engine filter (guide_call_records.voice_engine). Rows from before
// the engine was recorded count as the cascade; typed chat episodes run no
// voice engine, so any choice but All leaves them out.
export function EngineSelect({
  value,
  onChange,
}: {
  value: EngineFilter;
  onChange: (engine: EngineFilter) => void;
}) {
  return (
    <select
      className="engine-select"
      aria-label="Voice engine"
      title="Voice engine. Older calls count as the cascade; text-only chats are left out."
      value={value}
      onChange={(e) => onChange(e.target.value as EngineFilter)}
    >
      <option value="all">All engines</option>
      {ENGINE_ORDER.map((engine) => (
        <option key={engine} value={engine}>
          {ENGINE_LABEL[engine]}
        </option>
      ))}
    </select>
  );
}

// The cascade is the norm, so only another engine (and a fallback) gets a chip.
export function EngineChip({ call }: { call: CallRecord }) {
  const engine = call.voiceEngine;
  const fallback = call.voiceEngineFallback;
  if ((!engine || engine === "cascade") && !fallback) return null;
  const arm = call.voiceEngineArm ? ` · arm ${call.voiceEngineArm}` : "";
  return (
    <>
      {engine && engine !== "cascade" && (
        <span className="chip chip-engine" title={`Voice engine${arm}`}>
          {engineLabel(engine)}
        </span>
      )}
      {fallback && (
        <span className="chip chip-fallback" title={`Finished on the cascade: ${fallback}`}>
          fell back
        </span>
      )}
    </>
  );
}

// Compact recent-calls list used on the Dashboard and in the guide Insights
// tab. Rows link to the Calls page with the call pre-opened.
export function RecentCallsList({
  calls,
  onOpen,
  emptyText = "No calls in this range.",
}: {
  calls: CallRecord[];
  onOpen: (call: CallRecord) => void;
  emptyText?: string;
}) {
  if (calls.length === 0) return <div className="state-box">{emptyText}</div>;
  return (
    <div className="recent-calls">
      {calls.map((c) => (
        <button key={c.id} className="recent-call" onClick={() => onOpen(c)}>
          <SentimentDot score={c.sentimentScore} />
          <span className="recent-call-main">
            <span className="recent-call-guide">{c.guideName || c.guideId}</span>
            <span className="recent-call-meta">
              {formatDateTime(c.startedAt)} · {formatDurationMs(c.durationMs)}
            </span>
          </span>
          <ModeChip call={c} />
          <SourceChip source={c.source} />
          {c.hasRecording && (
            <span className="play-affordance" title="Recording available">
              ▶
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
